import clsx from "clsx";
import { saveAs } from "file-saver";
import moment from "moment";
import Papa from "papaparse";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import {
  ChartAggregateMetricIcon,
  ChartSplitMetricIcon,
} from "@features/sales-v3/charts/chart-configurator/chart-views-buttons";
import { type GetLandingPageReportChart_me_landingPageReportChart_rows } from "@north-beam/nb-client-side";
import { convertStartDateToTimeZone } from "@north-beam/nb-common";
import { IconButton, SecondaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";

interface Props {
  rows: GetLandingPageReportChart_me_landingPageReportChart_rows[];
}

export const ChartControls = ({ rows }: Props) => {
  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange,
      level,
      breakdownFilters,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    chartState: { chartView, alignAxesScale },
    metrics,
    setChartView,
    setAlignAxesScale,
  } = useLandingPageAnalyticsContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  return (
    <>
      <div className="flex flex-col justify-between">
        <div className="text-xs font-bold whitespace-nowrap mb-0.5">
          CHART VIEW
        </div>
        <div className="flex gap-2">
          <GenericTooltip
            content="Aggregate"
            noInfoCircle
            mouseEnterDelay={500}
            placement="top"
          >
            <IconButton
              className={clsx(
                {
                  "fill-white bg-cosmic border-transparent":
                    chartView === "aggregate",
                  "fill-interstellar hover:bg-cosmic-4 hover:border-cosmic+1":
                    chartView !== "aggregate",
                },
                "p-2 border rounded-lg h-8",
              )}
              onClick={() => {
                if (chartView === "aggregate") {
                  return;
                }

                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onClick",
                  additionalActionInfo: "Change chart view",
                  data: {
                    newChartView: "aggregate",
                    pageContext,
                  },
                });

                setChartView("aggregate");
              }}
            >
              {ChartSplitMetricIcon}
            </IconButton>
          </GenericTooltip>
          <GenericTooltip
            content="Split"
            noInfoCircle
            mouseEnterDelay={500}
            placement="top"
          >
            <IconButton
              className={clsx(
                {
                  "fill-white bg-cosmic border-transparent":
                    chartView === "split",
                  "fill-interstellar hover:bg-cosmic-4 hover:border-cosmic+1":
                    chartView !== "split",
                },
                "p-2 border rounded-lg h-8",
              )}
              onClick={() => {
                if (chartView === "split") {
                  return;
                }

                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onClick",
                  additionalActionInfo: "Change chart view",
                  data: {
                    newChartView: "split",
                    pageContext,
                  },
                });

                setChartView("split");
              }}
            >
              {ChartAggregateMetricIcon}
            </IconButton>
          </GenericTooltip>
        </div>
      </div>
      <div className="h-8 border-r"></div>
      <div>
        <div className="text-xs font-bold whitespace-nowrap mb-1">
          ALIGN AXES
        </div>
        <Switch
          id="align-axis"
          size="large"
          onChange={(val) => {
            prettyLogEventPage({
              domain: "Landing Page Analytics",
              action: "onChange",
              additionalActionInfo: "Change align axes scale",
              data: {
                newAlignAxesScale: val,
                pageContext,
              },
            });

            setAlignAxesScale(val);
          }}
          value={alignAxesScale}
        />
      </div>
      <div className="h-8 border-r"></div>
      <SecondaryButton
        className="font-bold flex items-center gap-2"
        onClick={() => {
          prettyLogEventPage({
            domain: "Landing Page Analytics",
            action: "onClick",
            additionalActionInfo: "Export chart data",
            data: {
              pageContext,
            },
          });

          const today = moment().startOf("day");
          const header: string[] =
            chartView === "split" ? ["date", "url", "name"] : ["date"];
          const entries: string[][] = [];

          for (const metric of metrics) {
            const name =
              metric.metadata.overviewPageMetricName ||
              metric.metadata.name ||
              metric.name;

            header.push(name);
          }

          for (const row of rows) {
            for (const dailyMetrics of row.metrics) {
              const date = moment(
                convertStartDateToTimeZone(dailyMetrics.date, user.timezone),
              );

              const entry: string[] = [date.format("YYYY-MM-DD")];

              if (chartView === "split") {
                entry.push(row.landingPage, row.name);
              }

              for (const metric of metrics) {
                const value = metric.calculate(dailyMetrics);
                let y = isNaN(value) ? 0 : value ?? 0;

                if (Math.abs(y) < 1e-6 || y === Infinity) {
                  y = 0;
                }

                entry.push(date.isAfter(today) ? "" : String(y));
              }

              entries.push(entry);
            }
          }

          if (entries.length > 0) {
            const csvString = Papa.unparse([header, ...entries]);

            const blob = new Blob([csvString], {
              type: "text/plain;charset=utf-8",
            });

            saveAs(blob, "landing-page-analytics-chart.csv");
          }
        }}
      >
        EXPORT CHART
        <i className="fa-regular fa-download"></i>
      </SecondaryButton>
    </>
  );
};
